import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { LoanApplicationTabId } from 'app/pages/loanApplication/LoanApplication.constants';
import { handleLoanFeesError } from 'app/pages/loanApplication/LoanApplication.utils';
import TitleHolderInfoPersonForm from 'app/pages/loanApplication/tabs/loanClosingCDS/shared/TitleHolderInfoPersonForm';
import _ from 'lodash';

import { ConfirmationModalType } from 'constants/confirmationDialog';
import { LONG_DATE_FORMAT } from 'constants/date';
import { MannerHeldType, TRUST_ENTITY_NAMES_NUMBER } from 'constants/loanApplication';
import useLoanId from 'hooks/useLoanId';
import { formatDate } from 'utils/date';

import {
  openConfirmationDialogAction,
  openDialogAction,
  showMessageAction,
} from 'store/global/actions';
import {
  deleteLoanTitleHolderAction,
  deleteTrustInfoAction,
  getLoanApplicationCommonDataByApplicationIdAction,
  getLoanApplicationHeaderViewAction,
} from 'store/loanApplications/actions';
import {
  selectExtendedBorrowers,
  selectExtendedNonBorrowerEntitiesCommonInfo,
  selectExtendedTitleHolders,
  selectTrustInfo,
} from 'store/loanApplications/selectors';

import { TitleHolderFieldId, TRUST_FIELD_NAMES } from '../Content.constants';

export const useTitleHolderHandlers = ({
  setIsLoading,
  setFormRef,
  mannerHeldOptions,
  initialFormValues,
}) => {
  const dispatch = useDispatch();
  const { applicationId } = useParams();
  const loanId = useLoanId();

  const borrowers = useSelector(selectExtendedBorrowers);
  const titleHolders = useSelector(selectExtendedTitleHolders);
  const nbes = useSelector(selectExtendedNonBorrowerEntitiesCommonInfo);
  const trustInfo = useSelector(selectTrustInfo);

  const isTrustInfoExist = !!trustInfo?.id;
  const [isTrustSectionVisible, setIsTrustSectionVisible] = useState(isTrustInfoExist);

  useEffect(() => {
    setIsTrustSectionVisible(isTrustInfoExist);
  }, [isTrustInfoExist]);

  const isOnlyPrimaryBorrower = borrowers.length === 1 && !titleHolders.length;
  const isBorrowersOrTitleHoldersExists = !!borrowers.length || !!titleHolders.length;

  const borrowersInfo = useMemo(
    () =>
      _.map(borrowers, ({ fullNameWithMiddleName, borrowerId }) => ({
        fullNameWithMiddleName,
        borrowerId,
      })),
    [borrowers],
  );

  const onAcceptTitleHolderDeletion = useCallback(
    actionData => {
      setIsLoading(true);

      dispatch(deleteLoanTitleHolderAction(actionData))
        .then(() => {
          dispatch(showMessageAction({ message: 'Title Holder removed', variant: 'success' }));
          dispatch(getLoanApplicationHeaderViewAction({ applicationId }));

          return dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId }));
        })
        .catch(({ response }) =>
          handleLoanFeesError({
            response,
            dispatch,
            onRetryAction: () => onAcceptTitleHolderDeletion(actionData),
          }),
        )
        .finally(() => setIsLoading(false));
    },
    [dispatch, applicationId, setIsLoading],
  );

  const onTitleHolderDelete = useCallback(
    e => {
      e.stopPropagation();

      const { titleHolderId } = e.currentTarget.dataset;

      const actionData = {
        titleHolderId,
        applicationId,
        tabId: LoanApplicationTabId.PROPERTY,
      };

      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Confirm title holder deletion',
        description: 'Are you sure you want to delete this title holder?',
        onAccept: () => onAcceptTitleHolderDeletion(actionData),
      };

      dispatch(openConfirmationDialogAction(params));
    },
    [dispatch, onAcceptTitleHolderDeletion, applicationId],
  );

  const onTitleHolderAdd = useCallback(
    e => {
      const { titleHolderId, isAddButton } = e.currentTarget.dataset;

      if (!titleHolderId && !isAddButton) {
        return;
      }

      const titleHolder = titleHolders.find(i => i.titleHolderId === titleHolderId);

      dispatch(
        openDialogAction({
          header: `${titleHolderId ? 'Edit' : 'Add'} title holder`,
          body: dialogProps => (
            <TitleHolderInfoPersonForm
              applicationId={applicationId}
              formRef={dialogProps.formRef}
              loanId={loanId}
              titleHolder={titleHolder}
            />
          ),
        }),
      );
    },
    [titleHolders, dispatch, applicationId, loanId],
  );

  const onAddTrust = useCallback(
    ({ setInForm, form }) => {
      const isOtherMannerHeldType =
        form?.[TitleHolderFieldId.MANNER_HELD]?.value === MannerHeldType.OTHER;

      if (!isOtherMannerHeldType) {
        const otherOption = _.find(mannerHeldOptions, { value: MannerHeldType.OTHER });

        if (otherOption) {
          setInForm(TitleHolderFieldId.MANNER_HELD, otherOption);
        }
      }

      setIsTrustSectionVisible(true);
    },
    [mannerHeldOptions],
  );

  const onAcceptTrustDeletion = useCallback(
    actionData => {
      setIsLoading(true);

      dispatch(deleteTrustInfoAction(actionData))
        .then(() => {
          dispatch(showMessageAction({ message: 'Trust removed', variant: 'success' }));

          if (setFormRef.current) {
            setFormRef.current(prevState => _.omit(prevState, TRUST_FIELD_NAMES));
          }

          return dispatch(getLoanApplicationCommonDataByApplicationIdAction({ applicationId }));
        })
        .catch(({ response }) =>
          handleLoanFeesError({
            response,
            dispatch,
            onRetryAction: () => onAcceptTrustDeletion(actionData),
          }),
        )
        .finally(() => setIsLoading(false));
    },
    [dispatch, applicationId, setFormRef, setIsLoading],
  );

  const onRemoveTrust = useCallback(
    ({ setForm }) => {
      if (!isTrustInfoExist) {
        setForm(prevState => {
          const clonedState = {
            ...prevState,
            [TitleHolderFieldId.MANNER_HELD]: isOnlyPrimaryBorrower
              ? initialFormValues?.[TitleHolderFieldId.MANNER_HELD]
              : null,
          };

          return _.omit(clonedState, TRUST_FIELD_NAMES);
        });

        setIsTrustSectionVisible(false);

        return;
      }

      const actionData = {
        applicationId,
        tabId: LoanApplicationTabId.PROPERTY,
      };

      const params = {
        type: ConfirmationModalType.WARNING,
        message: 'Confirm trust deletion',
        description: 'Are you sure you want to delete this trust?',
        onAccept: () => onAcceptTrustDeletion(actionData),
      };

      dispatch(openConfirmationDialogAction(params));
    },
    [
      applicationId,
      dispatch,
      initialFormValues,
      isOnlyPrimaryBorrower,
      isTrustInfoExist,
      onAcceptTrustDeletion,
    ],
  );

  const onCopyBorrowers = useCallback(
    ({ e, setInForm }) => {
      const { groupName } = e.currentTarget.dataset;

      Array.from({ length: TRUST_ENTITY_NAMES_NUMBER }).forEach((__, index) => {
        const { fullNameWithMiddleName, borrowerId } = borrowersInfo[index] || {
          fullNameWithMiddleName: null,
          borrowerId: null,
        };

        const orderNumber = index + 1;
        setInForm(`${groupName}${orderNumber}`, fullNameWithMiddleName);

        if (groupName !== 'beneficiary') {
          setInForm(`${groupName}${orderNumber}BorrowerId`, borrowerId);
        }
      });
    },
    [borrowersInfo],
  );

  const onPopulateVesting = useCallback(
    ({ form, setInForm }) => {
      const borrowerNames = _.map([...borrowers, ...titleHolders], 'fullNameWithMiddleName');
      const borrowersDescription = [
        borrowerNames.join(' and '),
        form[TitleHolderFieldId.VESTING_TYPE_ID]?.name,
      ]
        .filter(Boolean)
        .join(', ');

      const trusteeRecordNames = _.reduce(
        Array.from({ length: TRUST_ENTITY_NAMES_NUMBER }),
        (acc, _, index) => {
          const trustee = form[`trustee${index + 1}`];

          if (trustee) {
            acc.push(trustee);
          }

          return acc;
        },
        [],
      );

      const { trustName } = form;
      const establishedDate = form.trustEstablishedDate
        ? formatDate(form.trustEstablishedDate, LONG_DATE_FORMAT, { includeLocalOffset: true })
        : null;

      const trusteeEntityNames = _.map(
        [
          ..._.filter(nbes, b => b.isTrusteeOfTrust),
          ..._.filter(titleHolders, t => t.isTrusteeOfTrust),
        ],
        'fullNameWithMiddleName',
      );

      const trusteeNames = _.reduce(
        trusteeEntityNames,
        (acc, cur) => {
          const isDuplicated = _.some(acc, name => _.toLower(name) === _.toLower(cur));

          if (!isDuplicated) {
            acc.push(cur);
          }

          return acc;
        },
        trusteeRecordNames,
      );

      const hasTrustData = trustName && establishedDate && !!trusteeNames.length;

      const trustDescription = `${trusteeNames.join(' and ')}, Trustee${
        trusteeNames.length > 1 ? 's' : ''
      } of the ${trustName} Trust dated ${establishedDate}`;

      const vestingDescription = hasTrustData ? trustDescription : borrowersDescription;

      setInForm(TitleHolderFieldId.VESTING_DESCRIPTION, vestingDescription);
    },
    [borrowers, nbes, titleHolders],
  );

  return {
    borrowers,
    titleHolders,
    isTrustSectionVisible,
    isTrustInfoExist,
    isBorrowersOrTitleHoldersExists,
    onTitleHolderAdd,
    onTitleHolderDelete,
    onAddTrust,
    onRemoveTrust,
    onCopyBorrowers,
    onPopulateVesting,
  };
};
